/**
 * Portal Access Utility
 * @apal/shared-auth
 *
 * Critical utility for determining portal access based on user data.
 * This function is central to the multi-portal architecture.
 */

import type { AuthUser, PortalType, UserType } from "../types";

/**
 * Tier level requirements per portal
 * Based on RBAC matrix:
 * - APAL: Tier 1+ (standard, employee, team_lead, manager, vendor, executive, admin)
 * - HUMANDA: Tier 1+ for buyer, Tier 2+ for broker, Tier 3+ for seller, Tier 4+ for executive
 */
const PORTAL_USER_TYPES: Record<PortalType, UserType[]> = {
  APAL: ["standard", "employee", "team_lead", "manager", "vendor", "executive", "admin"],
  HUMANDA: ["buyer", "broker", "seller", "executive", "admin"],
};

/**
 * Check if a user has access to a specific portal
 *
 * @param user - The authenticated user, or null if not authenticated
 * @param portal - The portal to check access for
 * @returns Whether the user has access to the portal
 *
 * @example
 * ```ts
 * // Check APAL access
 * const canAccessApal = hasPortalAccess(user, "APAL");
 *
 * // Check HUMANDA access
 * const canAccessHumanda = hasPortalAccess(user, "HUMANDA");
 * ```
 */
export function hasPortalAccess(
  user: AuthUser | null,
  portal: PortalType
): boolean {
  // No user = no access
  if (!user) {
    return false;
  }

  // Primary check: Use explicit portal access flags from user profile
  // These come from JWT claims and are the authoritative source
  if (portal === "APAL") {
    return user.canAccessApal;
  }

  if (portal === "HUMANDA") {
    return user.canAccessHumanda;
  }

  // Unknown portal
  return false;
}

/**
 * Check if a user type is valid for a portal
 * This is a secondary validation used for UI rendering hints
 *
 * @param userType - The user type to check
 * @param portal - The portal to check access for
 * @returns Whether the user type is typically allowed for the portal
 */
export function isUserTypeValidForPortal(
  userType: UserType,
  portal: PortalType
): boolean {
  const validTypes = PORTAL_USER_TYPES[portal];
  return validTypes.includes(userType);
}

/**
 * Get all portals a user can access
 *
 * @param user - The authenticated user, or null if not authenticated
 * @returns Array of portals the user can access
 *
 * @example
 * ```ts
 * const portals = getAccessiblePortals(user);
 * // Returns: ["APAL"] or ["HUMANDA"] or ["APAL", "HUMANDA"] or []
 * ```
 */
export function getAccessiblePortals(user: AuthUser | null): PortalType[] {
  if (!user) {
    return [];
  }

  const portals: PortalType[] = [];

  if (user.canAccessApal) {
    portals.push("APAL");
  }

  if (user.canAccessHumanda) {
    portals.push("HUMANDA");
  }

  return portals;
}

/**
 * Check if a user has access to any portal
 *
 * @param user - The authenticated user, or null if not authenticated
 * @returns Whether the user has access to at least one portal
 */
export function hasAnyPortalAccess(user: AuthUser | null): boolean {
  if (!user) {
    return false;
  }

  return user.canAccessApal || user.canAccessHumanda;
}
